import type { RequestStream, SubscriptionResponse } from '@talos/kyoko';

/** A CoinMetrics-flavored `RequestStream`: `name` is the metric id (e.g. `'ReferenceRateUSD'`), `tag` is
 * the CoinMetrics asset id, and `frequency` is the CM stream frequency (e.g. `'1s'`). */
export type CoinMetricsRequestStream = RequestStream<{ frequency: string }>;

export interface CoinMetricsTick {
  cmAssetId: string;
  time: string;
  /** Raw decimal-string values for every requested metric present on this tick — see `parseAssetMetricTick`
   * for why these are never converted to a JavaScript `number` in this generic file. */
  metrics: Record<string, string>;
}

export interface CoinMetricsTickPayload {
  tag: string;
  time: string;
  metrics: Record<string, string>;
}

/** CoinMetrics' `/timeseries-stream/asset-metrics` endpoint accepts only these `frequency` values —
 * https://docs.coinmetrics.io -> Asset metrics (Timeseries Stream). Note this is specific to the
 * *streaming* endpoint: CM's REST/historical timeseries endpoint accepts a different set (`1m`, `5m`,
 * `1h`, `1d`, etc.) that would silently produce a connection this endpoint rejects if used here. */
export const COIN_METRICS_VALID_STREAM_FREQUENCIES = new Set(['1b', '1s', '200ms']);

/**
 * First reason `stream` isn't a well-formed CoinMetrics registration, or null if it's valid. Nothing in
 * `useSubscription`'s public type (`UseSubscriptionRequest`) ties a `{ client: 'cm' }` call to this
 * client's own required `frequency` field — a caller can compile a plain `{ name, tag }` RequestStream
 * with no frequency at all, which would otherwise silently resolve to a value CoinMetrics doesn't accept
 * and open a connection that fails forever with no clear indication why. Called at the registration
 * boundary (`CoinMetricsWebSocketClient.registerSubscription`/`updateSubscription`) so an invalid request
 * is rejected there, before ever reaching a physical connection.
 */
export function validateCoinMetricsRequestStream(stream: CoinMetricsRequestStream): string | null {
  if (!stream.name) {
    return 'a stream is missing a metric name';
  }
  if (!stream.tag) {
    return `metric "${stream.name}" is missing an asset tag`;
  }
  if (!stream.frequency || !COIN_METRICS_VALID_STREAM_FREQUENCIES.has(stream.frequency)) {
    return (
      `metric "${stream.name}" has an unsupported frequency "${stream.frequency}" — expected one of ` +
      [...COIN_METRICS_VALID_STREAM_FREQUENCIES].join(', ')
    );
  }
  return null;
}

/**
 * Exponential backoff delay for reconnecting the stream, matching `WebSocketClient`'s formula and
 * defaults (`packages/kyoko/src/providers/WebSocketClient.ts`) so both connections behave consistently.
 */
export function computeBackoffDelayMs(numRetries: number, initialRetryMs: number, maxRetryMs: number): number {
  return Math.min(maxRetryMs, initialRetryMs * Math.pow(2, numRetries));
}

/** Matches a well-formed CoinMetrics decimal-string metric value (optional sign, digits, optional
 * fractional part) — used only to reject genuinely malformed values, never to convert one. CoinMetrics'
 * `decimal` type allows up to 131,072 digits before the decimal point and 16,383 after (far beyond a
 * JavaScript double's ~15-17 significant digits of exact precision, or its ~1.8e308 overflow ceiling) —
 * CM returns these as JSON strings specifically "to preserve precision" (docs.coinmetrics.io), so
 * converting via `Number()` here would silently corrupt or drop a legitimate value. See `CoinMetricsTick`.
 */
const COIN_METRICS_DECIMAL_STRING_PATTERN = /^-?\d+(\.\d+)?$/;

/**
 * Parses one CoinMetrics `/timeseries-stream/asset-metrics` message (`{asset, time, <metric>: string,
 * cm_sequence_id}`) into `{ cmAssetId, time, metrics }`, extracting only the fields in `metricNames` —
 * null if the message is missing the asset id/time, or has no well-formed decimal value for any of them.
 * Metric values are kept as their original strings (see `COIN_METRICS_DECIMAL_STRING_PATTERN`) — a
 * consumer that needs a specific metric's value as a number makes that conversion itself, at the point it
 * actually knows what precision that metric requires, rather than one blanket lossy conversion applying
 * to every current and future CoinMetrics metric generically.
 */
export function parseAssetMetricTick(raw: unknown, metricNames: readonly string[]): CoinMetricsTick | null {
  if (typeof raw !== 'object' || raw == null) {
    return null;
  }
  const message = raw as Record<string, unknown>;
  const cmAssetId = message.asset;
  if (typeof cmAssetId !== 'string' || !cmAssetId) {
    return null;
  }
  const time = message.time;
  if (typeof time !== 'string' || !time) {
    return null;
  }

  const metrics: Record<string, string> = {};
  for (const metricName of metricNames) {
    const rawValue = message[metricName];
    if (typeof rawValue === 'string' && COIN_METRICS_DECIMAL_STRING_PATTERN.test(rawValue)) {
      metrics[metricName] = rawValue;
    }
  }
  if (Object.keys(metrics).length === 0) {
    return null;
  }

  return { cmAssetId, time, metrics };
}

export interface CoinMetricsErrorEnvelope {
  type: string;
  message: string;
}

/**
 * Parses a CoinMetrics WebSocket error envelope — `{ error: { type, message } }`, sent for the standard
 * CM error categories (unauthorized API key, forbidden resource, invalid request, rate limit exceeded),
 * as well as the slow-client warning-then-error-then-disconnect sequence
 * (https://docs.coinmetrics.io -> API Conventions / Rate limits). Distinct from a tick (which has no
 * `error` field) — checked first in `openConnection`'s message handler, since an error envelope is
 * affirmative evidence of a real problem and should count toward persistent-failure detection in its own
 * right, rather than being treated as "not a tick yet" and silently resetting that counter.
 */
export function parseCoinMetricsErrorEnvelope(raw: unknown): CoinMetricsErrorEnvelope | null {
  if (typeof raw !== 'object' || raw == null) {
    return null;
  }
  const error = (raw as Record<string, unknown>).error;
  if (typeof error !== 'object' || error == null) {
    return null;
  }
  const { type, message } = error as Record<string, unknown>;
  if (typeof type !== 'string' || typeof message !== 'string') {
    return null;
  }
  return { type, message };
}

/**
 * The physical connection a single stream belongs under. CoinMetrics' frequency is a connection-level
 * param — it can't be unioned across different frequencies the way assets/metrics can — so each distinct
 * frequency gets its own connection. Assumes `stream.frequency` is present and valid: every stream
 * reaching this point has already passed `validateCoinMetricsRequestStream` at the registration boundary.
 * Operates per-stream (not per-address) precisely because one address's own registration can mix
 * frequencies across its streams — see `groupStreamsByFrequency`, which is what actually splits them.
 */
export function computeConnectionKeyForStream(stream: CoinMetricsRequestStream): string {
  return stream.frequency;
}

/**
 * Splits one address's streams by frequency. An address can register streams at more than one frequency
 * in a single call (e.g. price at `1s` and a future metric at `1m`) — since CoinMetrics can't serve
 * mixed frequencies over one connection, each distinct frequency among this address's own streams needs
 * its own connection group, or it would silently be sent (and received) at the wrong cadence.
 */
export function groupStreamsByFrequency(
  streams: readonly CoinMetricsRequestStream[]
): Map<string, CoinMetricsRequestStream[]> {
  const grouped = new Map<string, CoinMetricsRequestStream[]>();
  for (const stream of streams) {
    const key = computeConnectionKeyForStream(stream);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(stream);
    } else {
      grouped.set(key, [stream]);
    }
  }
  return grouped;
}

/** Deduped, sorted assets + metrics referenced across every address's streams sharing one connection key. */
export function computeMergedAssetsAndMetrics(
  streamsByAddress: ReadonlyMap<string, readonly CoinMetricsRequestStream[]>
): {
  assets: string[];
  metrics: string[];
} {
  const assets = new Set<string>();
  const metrics = new Set<string>();
  for (const streams of streamsByAddress.values()) {
    for (const stream of streams) {
      assets.add(stream.tag);
      metrics.add(stream.name);
    }
  }
  return { assets: [...assets].toSorted(), metrics: [...metrics].toSorted() };
}

/** Canonical, order-independent string for diffing "does the physical connection need to change". */
export function canonicalizeAssetsAndMetrics({ assets, metrics }: { assets: string[]; metrics: string[] }): string {
  return `assets:${assets.toSorted().join(',')}|metrics:${metrics.toSorted().join(',')}`;
}

export function buildConnectionUrl({
  baseUrl,
  apiKey,
  assets,
  metrics,
  frequency,
}: {
  baseUrl: string;
  apiKey: string;
  assets: readonly string[];
  metrics: readonly string[];
  frequency: string;
}): string {
  return (
    `${baseUrl}/timeseries-stream/asset-metrics` +
    `?assets=${encodeURIComponent(assets.join(','))}` +
    `&metrics=${encodeURIComponent(metrics.join(','))}` +
    `&frequency=${encodeURIComponent(frequency)}` +
    `&backfill=latest` +
    `&api_key=${encodeURIComponent(apiKey)}`
  );
}

/** CoinMetrics documents a hard limit on the request URI for `/timeseries-stream/asset-metrics`
 * ("must not be greater than 10 000 symbols"), returning `414 UriTooLong` beyond it
 * (https://docs.coinmetrics.io -> Asset metrics (Timeseries Stream)). */
export const COIN_METRICS_MAX_URI_LENGTH = 10000;

/** Subtracted from `COIN_METRICS_MAX_URI_LENGTH` before chunking, as headroom against any off-by-one in
 * exact character accounting (e.g. proxy/browser URL normalization) — not because CoinMetrics documents
 * a smaller limit. */
export const COIN_METRICS_URI_LENGTH_SAFETY_MARGIN = 200;

export interface AssetUrlChunkResult {
  /** Ordered asset chunks, each producing a connection URL within `maxUrlLength`. */
  chunks: string[][];
  /** Assets that don't fit into any chunk — even alone, that asset's URL (with the same shared `metrics`,
   * `frequency`, `apiKey`, `baseUrl` every chunk carries) already exceeds `maxUrlLength`. Only reachable
   * when the *shared* overhead (an unusually large merged `metrics` list, most plausibly) is itself
   * responsible, since sharding assets alone can't help there — see `chunkAssetsByUrlLength`'s doc.
   */
  oversizedAssets: string[];
}

/**
 * Splits `assets` into the fewest ordered chunks such that each chunk's resulting connection URL (built
 * via `buildConnectionUrl` with the same `metrics`/`frequency`/`apiKey`, which are shared by every
 * chunk) stays within `maxUrlLength`. Greedy and deterministic — assets are processed in the order
 * given (callers should pass them pre-sorted, as `computeMergedAssetsAndMetrics` already returns them),
 * so the same input always produces the same chunking. This does not attempt to keep chunk *assignment*
 * stable when the input set itself changes (e.g. adding one asset near the start of a large sorted list
 * can shift later assets into a different chunk) — some extra reconnects on a shape-changing edit are an
 * acceptable tradeoff for a simple, correct algorithm, since staying under CoinMetrics' URI limit is the
 * actual correctness requirement, not perfectly stable packing.
 *
 * Splitting only ever reduces `assets` per chunk, not `metrics` — if `metrics` (plus `apiKey`/`baseUrl`
 * overhead) alone already exceeds `maxUrlLength` for a single asset, no amount of asset-splitting fixes
 * that. Rather than still emit a chunk guaranteed to get CoinMetrics' 414, that asset is reported in
 * `oversizedAssets` instead — callers (see `CoinMetricsWebSocketClient.reconcile`) surface this as a
 * subscription failure for the affected registrations rather than opening a doomed connection.
 */
export function chunkAssetsByUrlLength({
  baseUrl,
  apiKey,
  assets,
  metrics,
  frequency,
  maxUrlLength = COIN_METRICS_MAX_URI_LENGTH - COIN_METRICS_URI_LENGTH_SAFETY_MARGIN,
}: {
  baseUrl: string;
  apiKey: string;
  assets: readonly string[];
  metrics: readonly string[];
  frequency: string;
  maxUrlLength?: number;
}): AssetUrlChunkResult {
  if (assets.length === 0) {
    return { chunks: [], oversizedAssets: [] };
  }

  const chunks: string[][] = [];
  const oversizedAssets: string[] = [];
  let currentChunk: string[] = [];

  for (const asset of assets) {
    const soloUrl = buildConnectionUrl({ baseUrl, apiKey, assets: [asset], metrics, frequency });
    if (soloUrl.length > maxUrlLength) {
      oversizedAssets.push(asset);
      continue;
    }

    const candidateChunk = [...currentChunk, asset];
    const candidateUrl = buildConnectionUrl({ baseUrl, apiKey, assets: candidateChunk, metrics, frequency });
    if (candidateUrl.length > maxUrlLength && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = [asset];
    } else {
      currentChunk = candidateChunk;
    }
  }
  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return { chunks, oversizedAssets };
}

/**
 * Partitions one frequency's addresses into groups such that merging an address's streams into a group
 * never pushes an asset *already* in that group over `maxUrlLength` — without this, one address adding
 * enough metrics could make every asset at that frequency (via `computeMergedAssetsAndMetrics`'s global
 * union) classify as oversized, closing other addresses' already-healthy connections for a metric increase
 * they had nothing to do with. An address whose streams would break an existing group is isolated into its
 * own separate group instead, so its oversized metric footprint can only ever affect itself.
 *
 * Greedy and deterministic — addresses are processed in `streamsByAddress`'s own iteration order
 * (registration order), placed into the first group they fit cleanly into, else a new group of their own.
 * This can produce more connections than a theoretically optimal packing (two isolated addresses that
 * would actually fit together each get their own group) — an acceptable tradeoff for a simple, correct
 * algorithm, the same one `chunkAssetsByUrlLength` itself already makes for asset-chunk assignment.
 *
 * Returns each group's own `streamsByAddress`, not just its merged `{assets, metrics}` — two *different*
 * groups can legitimately end up referencing the same asset (that's the whole point of isolating one
 * address's oversized footprint rather than rejecting it outright), so anything routing a tick or a
 * failure notification must scope itself to one group's own membership, never match by asset alone across
 * every group at a frequency, or a failure/tick on one group would incorrectly reach an address whose
 * actual subscription lives in a different, unrelated group.
 */
export function partitionAddressesToAvoidCrossSubscriberEviction({
  baseUrl,
  apiKey,
  frequency,
  streamsByAddress,
  maxUrlLength,
}: {
  baseUrl: string;
  apiKey: string;
  frequency: string;
  streamsByAddress: ReadonlyMap<string, readonly CoinMetricsRequestStream[]>;
  maxUrlLength?: number;
}): Array<{ assets: string[]; metrics: string[]; streamsByAddress: Map<string, CoinMetricsRequestStream[]> }> {
  const groups: Map<string, CoinMetricsRequestStream[]>[] = [];

  for (const [address, streams] of streamsByAddress) {
    let placed = false;
    for (const group of groups) {
      const candidate = new Map(group);
      candidate.set(address, [...streams]);
      const merged = computeMergedAssetsAndMetrics(candidate);
      const { oversizedAssets } = chunkAssetsByUrlLength({
        baseUrl,
        apiKey,
        frequency,
        assets: merged.assets,
        metrics: merged.metrics,
        ...(maxUrlLength != null ? { maxUrlLength } : {}),
      });
      // Only accept a fully clean merge — not just "no PRE-EXISTING asset broke" — so an address whose
      // own asset would be oversized purely from joining this group (as opposed to being oversized on its
      // own regardless of any group) gets a chance to try the next group, or its own, instead.
      if (oversizedAssets.length === 0) {
        group.set(address, [...streams]);
        placed = true;
        break;
      }
    }
    if (!placed) {
      groups.push(new Map([[address, [...streams]]]));
    }
  }

  return groups.map(group => ({ ...computeMergedAssetsAndMetrics(group), streamsByAddress: group }));
}

/**
 * Filters `streamsByAddress` down to just the streams (and only the addresses with at least one matching
 * stream) whose `tag` is in `assets`. A group large enough to need more than one URL-length shard
 * (`chunkAssetsByUrlLength`) would otherwise have every shard share the group's *entire* address
 * membership — meaning dropping or failing one shard could still wrongly notify an address whose stream
 * actually lives in a different, healthy shard of that same group. Scoping each shard to only the
 * addresses that actually reference its own assets closes that gap.
 */
export function filterStreamsByAddressToAssets(
  streamsByAddress: ReadonlyMap<string, readonly CoinMetricsRequestStream[]>,
  assets: readonly string[]
): Map<string, CoinMetricsRequestStream[]> {
  const assetSet = new Set(assets);
  const filtered = new Map<string, CoinMetricsRequestStream[]>();
  for (const [address, streams] of streamsByAddress) {
    const matchingStreams = streams.filter(stream => assetSet.has(stream.tag));
    if (matchingStreams.length > 0) {
      filtered.set(address, matchingStreams);
    }
  }
  return filtered;
}

/** Maps each CM asset id to the set of addresses whose streams reference it, for one connection's
 * registrations — used to dispatch an incoming tick only to the subscriptions that asked for it. */
export function buildReverseIndex(
  streamsByAddress: ReadonlyMap<string, readonly CoinMetricsRequestStream[]>
): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();
  for (const [address, streams] of streamsByAddress) {
    for (const stream of streams) {
      const addresses = index.get(stream.tag);
      if (addresses) {
        addresses.add(address);
      } else {
        index.set(stream.tag, new Set([address]));
      }
    }
  }
  return index;
}

/** Metric names a given address's streams requested for `cmAssetId` — keeps one subscriber's callback
 * from ever seeing a metric only a different subscriber asked for on a shared physical connection. */
export function getMetricNamesForAddress(streams: readonly CoinMetricsRequestStream[], cmAssetId: string): string[] {
  return streams.filter(stream => stream.tag === cmAssetId).map(stream => stream.name);
}

/**
 * Synthesizes a Talos-shaped `SubscriptionResponse` for one address from a parsed CM tick, filtered to
 * only the metrics that address's own streams requested. Returns null when none of those metrics are
 * present on this tick — routing (`buildReverseIndex`) is indexed by asset only, since one physical
 * connection can serve multiple addresses wanting different metrics for the same asset, and CoinMetrics
 * can omit/null an unsupported or not-currently-available metric on any given tick — so a tick meant for
 * one address's metric would otherwise still reach every other address sharing that asset, with an empty
 * `metrics: {}` payload. `initial`/`next`/`page`/`action` stay unset — CoinMetrics ticks are always live
 * deltas, never paginated.
 */
export function buildTickResponse({
  type,
  reqid,
  tick,
  metricNames,
}: {
  type: string;
  reqid: number;
  tick: CoinMetricsTick;
  metricNames: readonly string[];
}): SubscriptionResponse<CoinMetricsTickPayload> | null {
  const metrics: Record<string, string> = {};
  for (const metricName of metricNames) {
    const value = tick.metrics[metricName];
    if (value != null) {
      metrics[metricName] = value;
    }
  }
  if (Object.keys(metrics).length === 0) {
    return null;
  }
  return {
    reqid,
    type,
    tag: tick.cmAssetId,
    ts: tick.time,
    data: [{ tag: tick.cmAssetId, time: tick.time, metrics }],
  };
}
